import { PAYMENT_OPTION_TO_DISPLAY_NAME } from "@shared/features/settings/subscription/constants";
import TextWithIcon from "common/Text/TextWithIcon";
import Form from "common/Form/Form";
import { EMAIL_FORM_ITEM_RULES } from "features/auth/constants";
import { FormInstance } from "antd/es/form";
import Input from "antd/es/input";
import Statistic from "antd/es/statistic";
import Select from "common/Select/Select";
import Text from "common/Typography/Text";
import React, {
  ReactNode,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BuySubscriptionContext } from "features/settings/Subscription/BuySubscription.context.tsx";
import { sharedLib } from "@shared/services/shared.service";
import { CheckoutForm } from "features/settings/Subscription/styles";
import {
  AnalyticsPageName,
  GetPaymentLinkRequest,
  InvoiceStatus,
  LanguageNameSpaces,
  MutationStatus,
  PaymentLinkData,
  PaymentLinkStatus,
  SubscriptionPaymentMethodType,
} from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { useUser } from "@shared/utils/hooks/misc";
import styled, { useTheme } from "styled-components";
import Flex from "common/Boxes/Flex";
import { typography } from "@shared/common/Themes/typography";
import InputBox from "common/Inputs/InputBox";
import Button from "common/Buttons/Button";
import EqualColumnsGrid from "common/Grids/EqualColumnsGrid";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import { copyCodeToClipboard, openMessage, showIntercom } from "utils/misc";
import { getCopyMessage } from "@shared/common/CopyToClipboard/utils";
import { useIsSmallMobileCheckout } from "features/settings/common/hooks";
import {
  BILLING_HISTORY_QUERY_PARAM,
  EMAIL_QUERY_PARAM,
  PROMO_CODE_QUERY_PARAM,
  SMALL_MOBILE_CHECKOUT_HEIGHT,
} from "features/settings/common/constants";
import useHistoryWithQuery from "utils/hooks/useHistoryWithQuery";
import useQuery from "utils/hooks/useQuery";
import {
  addLocalStoragePayment,
  findLocalStoragePayment,
  getParsedStorageRequest,
  removeLocalStoragePayment,
} from "features/settings/Subscription/utils";
import { LocalStoragePaymentData } from "features/settings/common/types";
import CopyOutlined from "@ant-design/icons/CopyOutlined";
import { HelperTooltip } from "common/Tooltips/HelperTooltip";
import Link from "common/Typography/Link";
import { isEqual } from "lodash";
import { useHistory } from "react-router-dom";
import LinkButton from "common/Buttons/LinkButton";
import CornixTerms from "features/settings/Subscription/CornixTerms.tsx";
import { IS_TELEGRAM_WEB_APP } from "features/telegram/constants";
import { telegramOpenLink } from "features/telegram/utils.ts";
import { ASSET_MANAGER_TERMS_OF_USE_LINK } from "@shared/common/constants.ts";
import { isWhitelabelSubscriptionType } from "@shared/utils/misc.ts";

interface CryptoCheckoutProps {
  form: FormInstance;
  setEmail: (email?: string) => void;
  email?: string;
  getPaymentLink: () => void;
  loadingPaymentLink?: boolean;
  expirationText: ReactNode;
  promoCodeRow?: ReactNode;
  baseRequest: GetPaymentLinkRequest | undefined;
  getPaymentLinkStatus: MutationStatus<PaymentLinkData, GetPaymentLinkRequest>;
  acceptedTerms: boolean;
  setAcceptedTerms: (acceptedTerms: boolean) => void;
}

const CryptoCheckout = ({
  form,
  setEmail,
  email,
  getPaymentLink,
  expirationText,
  promoCodeRow,
  baseRequest,
  getPaymentLinkStatus,
  acceptedTerms,
  setAcceptedTerms,
}: CryptoCheckoutProps) => {
  const {
    paymentMethod,
    setPaymentMethod,
    setCryptoTotalPrice,
    paymentMethods,
    plan,
  } = useContext(BuySubscriptionContext)!;
  const termsLink = isWhitelabelSubscriptionType(plan?.subscription_plan_type)
    ? ASSET_MANAGER_TERMS_OF_USE_LINK
    : undefined;
  const { t } = useTypedTranslation(LanguageNameSpaces.BILLING);
  const theme = useTheme();
  const { user } = useUser();
  const [getPaymentStatus, getPaymentStatusStatus] =
    sharedLib.sharedApi.settings.useGetPaymentStatusMutation();
  const { data: paymentStatus, isLoading: paymentStatusLoading } =
    getPaymentStatusStatus;

  const {
    data: lastPaymentLinkData,
    isLoading: paymentLinkLoading,
    originalArgs,
  } = getPaymentLinkStatus;
  const [paymentLinkData, setPaymentLinkData] = useState<
    PaymentLinkStatus | undefined
  >();
  const [baseShowLoading, setBaseShowLoading] = useState(false);
  const isSmallMobile = useIsSmallMobileCheckout();
  const { appendQuery } = useHistoryWithQuery();
  const query = useQuery();
  const queryEmail = query.get(EMAIL_QUERY_PARAM);
  const [step, setStep] = useState<Step>(
    acceptedTerms && (user?.email || queryEmail) ? Step.CHECKOUT : Step.EMAIL
  );
  const [isErrorContent, setIsErrorContent] = useState(false);
  const history = useHistory();
  const getPaymentLinkRef = useRef<() => void>(undefined);
  getPaymentLinkRef.current = getPaymentLink;

  const copyName = t("crypto_checkout.payment_address");
  const paymentAddress = paymentLinkData?.payment_address || "";
  const isEmailStep = step === Step.EMAIL;
  const isCheckoutStep = step === Step.CHECKOUT;
  const isLoading = paymentLinkLoading || paymentStatusLoading;
  const showLoading =
    paymentLinkLoading ||
    !paymentLinkData ||
    (paymentStatusLoading && baseShowLoading);

  const getPaymentTimeLeftMinutes = (
    paymentStatus?: PaymentLinkStatus | LocalStoragePaymentData
  ) => {
    if (!paymentStatus?.time_expires) return;
    const remainingMilliSeconds =
      paymentStatus.time_expires * 1000 - new Date().getTime();
    return remainingMilliSeconds / 1000 / 60;
  };

  const getStorageDefaultInvoiceId = () => {
    if (baseRequest && isCheckoutStep) {
      const storagePaymentData = findLocalStoragePayment(baseRequest);
      if (storagePaymentData) {
        const timeLeft = getPaymentTimeLeftMinutes(storagePaymentData);
        if (timeLeft !== undefined && timeLeft / 60 < -12) {
          removeLocalStoragePayment(storagePaymentData.invoice_id);
        } else {
          return storagePaymentData;
        }
      }
    }
  };

  const storageDefaultPaymentData = useMemo(
    () => getStorageDefaultInvoiceId(),
    [baseRequest, isCheckoutStep]
  );

  const [activeInvoiceId, setActiveInvoiceId] = useState<string | undefined>(
    storageDefaultPaymentData?.invoice_id
  );

  const queryStatus = storageDefaultPaymentData
    ? getPaymentStatusStatus
    : getPaymentLinkStatus;

  useLayoutEffect(() => {
    if (queryStatus.isError || !paymentLinkData) {
      setCryptoTotalPrice(undefined);
    } else {
      setCryptoTotalPrice(paymentLinkData?.amount || "0");
    }
    return () => {
      setCryptoTotalPrice(undefined);
    };
  }, [paymentLinkData, queryStatus.isError]);

  useLayoutEffect(() => {
    if (queryStatus.isError) {
      setIsErrorContent(true);
    } else if (!queryStatus.isLoading) {
      setIsErrorContent(false);
    }
  }, [queryStatus.isError, queryStatus.isLoading]);

  useLayoutEffect(() => {
    if (isCheckoutStep && baseRequest) {
      if (storageDefaultPaymentData?.invoice_id) {
        setActiveInvoiceId(storageDefaultPaymentData?.invoice_id);
      } else {
        getPaymentLinkRef.current?.();
      }
    }
  }, [isCheckoutStep, baseRequest]);

  useEffect(() => {
    if (isCheckoutStep) {
      if (email) {
        appendQuery({ [EMAIL_QUERY_PARAM]: email });
      }
    } else if (queryEmail) {
      appendQuery(undefined, [EMAIL_QUERY_PARAM]);
    }
  }, [step]);

  useEffect(() => {
    const invoiceId = lastPaymentLinkData?.invoice_id;
    if (invoiceId && originalArgs) {
      addLocalStoragePayment(
        invoiceId,
        originalArgs,
        lastPaymentLinkData.time_expires
      );
      if (
        baseRequest &&
        isEqual(
          getParsedStorageRequest(originalArgs),
          getParsedStorageRequest(baseRequest)
        )
      ) {
        setActiveInvoiceId(invoiceId);
      }
    }
  }, [lastPaymentLinkData]);

  useEffect(() => {
    if (!isLoading) setBaseShowLoading(false);
  }, [isLoading]);

  useLayoutEffect(() => {
    if (paymentStatus) {
      if (paymentStatus.status === InvoiceStatus.PAID) {
        openMessage(t("crypto_checkout.payment_success"), "success");
        history.push(
          `${BILLING_HISTORY_QUERY_PARAM}?${PROMO_CODE_QUERY_PARAM}=1`
        );
      } else {
        setPaymentLinkData(paymentStatus);
      }
    }
  }, [paymentStatus]);

  useEffect(() => {
    if (
      isCheckoutStep &&
      activeInvoiceId &&
      !isErrorContent &&
      !paymentLinkLoading
    ) {
      setBaseShowLoading(true);
      getPaymentStatus({ invoice_id: activeInvoiceId });
      const interval = setInterval(() => {
        getPaymentStatus({ invoice_id: activeInvoiceId });
      }, 20000);
      return () => {
        clearInterval(interval);
      };
    }
  }, [isCheckoutStep, activeInvoiceId, isErrorContent, paymentLinkLoading]);

  const networkOptions = paymentMethods
    ?.filter(
      (method) =>
        method.payment_option !==
        PAYMENT_OPTION_TO_DISPLAY_NAME[SubscriptionPaymentMethodType.PAYPAL]
    )
    .map((method) => {
      return {
        value: method.id,
        label: (
          <TextWithIcon
            text={method.payment_option}
            iconLink={method.icon_url}
          />
        ),
        paymentMethod: method,
      };
    });

  const onRegenerateClick = () => {
    setBaseShowLoading(true);
    removeLocalStoragePayment(undefined, baseRequest);
    getPaymentLink();
  };

  const getErrorsData = () => {
    const paymentTimeLeft = getPaymentTimeLeftMinutes(paymentLinkData);
    const isBeforeExpirationWarning =
      paymentTimeLeft !== undefined &&
      paymentTimeLeft < 10 &&
      paymentTimeLeft > 0;
    const isExpired = paymentTimeLeft !== undefined && paymentTimeLeft < 0;
    const isPartialPayment =
      paymentLinkData &&
      paymentLinkData.received &&
      parseFloat(paymentLinkData.received) > 0 &&
      paymentLinkData.status !== InvoiceStatus.PAID;

    let color: string | undefined;
    let errorText: string | undefined;
    let errorTitle: string | undefined;
    let copyAddressWarning: string | undefined;
    let baseTooltipDescription: string | undefined;
    if (isExpired) {
      color = theme.colors.red;
      errorText = t("crypto_checkout.payment_expired", {
        partial: isPartialPayment ? t("crypto_checkout.partial") : "",
      });
      const baseDescription = isPartialPayment
        ? t("crypto_checkout.partial_payment_instructions")
        : t("crypto_checkout.payment_expired_instructions");
      copyAddressWarning =
        baseDescription + " " + t("crypto_checkout.below_suffix");
      baseTooltipDescription =
        baseDescription + " " + t("crypto_checkout.above_suffix");
      errorTitle = t("crypto_checkout.payment_expired_title");
    } else if (isBeforeExpirationWarning) {
      color = theme.colors.darkYellow;
      errorText = t("crypto_checkout.payment_about_expire");
      baseTooltipDescription = t(
        "crypto_checkout.payment_about_expire_instructions"
      );
      errorTitle = t("crypto_checkout.payment_about_expire_title");
    }
    const tooltipDescription = (
      <>
        {baseTooltipDescription}
        <div>
          {t("crypto_checkout.contact_support_prefix")}{" "}
          <Link onClick={showIntercom}>
            {t("crypto_checkout.contact_support_link")}
          </Link>{" "}
          {t("crypto_checkout.contact_support_suffix")}
        </div>
      </>
    );
    return {
      color,
      errorText,
      isExpired,
      copyAddressWarning,
      tooltipDescription,
      errorTitle,
    };
  };

  const errorsData = getErrorsData();

  const getQrRow = () => {
    return (
      <Flex
        $justifyContent="flex-start"
        $alignItems="flex-start"
        style={{ marginBottom: isSmallMobile ? 8 : 12 }}
      >
        <ImageWrapper
          $imageSize={isSmallMobile ? 90 : 120}
          $isSmallMobile={isSmallMobile}
        >
          <QrImage
            src={paymentLinkData?.qr_url}
            alt={t("crypto_checkout.qr_alt")}
            style={errorsData.isExpired ? { filter: "blur(4px)" } : undefined}
          />
          <ImageBorder $isSmallMobile={isSmallMobile} />
        </ImageWrapper>
        <div
          style={{
            marginLeft: 8,
            width: "100%",
            marginTop: isSmallMobile ? 5 : 8,
          }}
        >
          <InputTitle>{t("crypto_checkout.time_left")}</InputTitle>
          <CountdownInput
            $isSmallMobile={isSmallMobile}
            $borderColor={errorsData.color}
          >
            <Statistic.Timer
              type="countdown"
              value={(paymentLinkData?.time_expires || 0) * 1000}
              valueStyle={{
                fontSize: typography.fontSizes.M,
                color: theme.colors.primaryText,
              }}
            />
            <LinkButton
              type="link"
              onClick={onRegenerateClick}
              style={{ height: "100%" }}
            >
              {t("crypto_checkout.regenerate")}
            </LinkButton>
          </CountdownInput>
          {errorsData.errorText && (
            <Text as="div" style={{ color: errorsData.color, marginTop: 4 }}>
              {errorsData.errorText}
              <HelperTooltip
                description={errorsData.tooltipDescription}
                analyticsPageName={AnalyticsPageName.BILLING}
                analyticsFieldName={errorsData.errorTitle}
              />
            </Text>
          )}
        </div>
      </Flex>
    );
  };

  const getAmountsRow = () => {
    return (
      <>
        <EqualColumnsGrid numColumns={2} style={{ marginBottom: 12 }}>
          <div>
            <InputTitle>{t("crypto_checkout.remaining_amount")}</InputTitle>
            <AmountInput>
              {parseFloat(paymentLinkData?.remaining || "0")}
            </AmountInput>
          </div>
          <div>
            <InputTitle>{t("crypto_checkout.total_received")}</InputTitle>
            <AmountInput>
              {parseFloat(paymentLinkData?.received || "0")}
            </AmountInput>
          </div>
        </EqualColumnsGrid>
      </>
    );
  };

  const networkSelector = (
    <div>
      <Form.Item
        shouldUpdate
        name="network"
        label={t("crypto_checkout.network")}
        rules={[
          {
            required: true,
            message: t("crypto_checkout.choose_network"),
          },
        ]}
        style={{ marginBottom: 0 }}
      >
        <Select
          showSearch={false}
          style={{ width: "100%" }}
          onChange={(_, option: any) => {
            setPaymentMethod(option.paymentMethod);
          }}
          options={networkOptions}
          mobileTitle={t("crypto_checkout.network")}
        />
      </Form.Item>
      {paymentMethod?.payment_option.includes("TRC20") && (
        <WarningText>{t("crypto_checkout.trc20_warning")}</WarningText>
      )}
    </div>
  );

  const recipientAddress = (
    <div>
      <InputTitle>{t("crypto_checkout.payment_address")}</InputTitle>
      <InputBox
        style={{ marginBottom: 12 }}
        onClick={() => {
          if (errorsData.copyAddressWarning) {
            openMessage(errorsData.copyAddressWarning || "", "warning");
          } else {
            copyCodeToClipboard(paymentAddress);
            openMessage(getCopyMessage(copyName), "info");
          }
        }}
      >
        <Text
          type="secondary"
          $ellipsis={true}
          style={{
            flex: 1,
            filter: errorsData.isExpired ? "blur(2px)" : undefined,
          }}
        >
          {paymentAddress}
        </Text>
        <CopyOutlined style={{ paddingLeft: 8 }} />
      </InputBox>
    </div>
  );

  const getPaymentDetails = () => {
    if (!paymentLinkData) return;
    return (
      <div>
        {promoCodeRow}
        <EqualColumnsGrid
          numColumns={isSmallMobile ? 2 : 1}
          style={{ marginTop: 8 }}
        >
          {networkSelector}
          {recipientAddress}
        </EqualColumnsGrid>
        {getQrRow()}
        {getAmountsRow()}
      </div>
    );
  };

  const emailFormItem = user?.email ? undefined : (
    <Form.Item
      name="email"
      label={
        <Flex style={{ width: "100%", marginBottom: -4 }}>
          {t("crypto_checkout.email")}
          {!isEmailStep && (
            <LinkButton
              type="link"
              onClick={() => setStep(Step.EMAIL)}
              style={{ paddingRight: 0 }}
            >
              {t("crypto_checkout.update")}
            </LinkButton>
          )}
        </Flex>
      }
      initialValue={queryEmail}
      rules={EMAIL_FORM_ITEM_RULES}
      style={{ marginBottom: 10 }}
    >
      <Input
        onChange={(e) => setEmail(e.target.value)}
        disabled={isCheckoutStep}
      />
    </Form.Item>
  );

  const getEmailStepContent = () => {
    return (
      <>
        {emailFormItem}
        <CornixTerms
          initialValue={acceptedTerms}
          onChange={setAcceptedTerms}
          termsLink={termsLink}
        />
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: "100%", marginTop: 6 }}
        >
          {t("crypto_checkout.continue")}
        </Button>
      </>
    );
  };

  const openPaymentLink = () => {
    if (!paymentLinkData?.payment_url) return;
    if (IS_TELEGRAM_WEB_APP) {
      try {
        telegramOpenLink(paymentLinkData.payment_url);
      } catch {}
    } else {
      window.open(paymentLinkData?.payment_url);
    }
  };

  const onErrorTryAgainClick = () => {
    if (!storageDefaultPaymentData) {
      getPaymentLink();
    } else if (activeInvoiceId) {
      getPaymentStatus({ invoice_id: activeInvoiceId });
    }
  };

  const getQueryErrorContent = () => {
    return (
      <>
        {emailFormItem}
        {promoCodeRow}
        <div style={{ marginTop: 8 }}>{networkSelector}</div>
        <ErrorContentWrapper>
          <Text type="secondary" style={{ textAlign: "center" }}>
            {t("crypto_checkout.fetch_error")}
          </Text>
          <Button
            type="primary"
            style={{ width: "fit-content", marginTop: 16 }}
            onClick={onErrorTryAgainClick}
            loading={
              storageDefaultPaymentData
                ? paymentStatusLoading
                : paymentLinkLoading
            }
          >
            {t("crypto_checkout.try_again")}
          </Button>
        </ErrorContentWrapper>
      </>
    );
  };

  const getCheckoutStepContent = () => {
    if (isErrorContent) return getQueryErrorContent();
    return (
      <>
        {emailFormItem}
        <LoadingSpinnerOverlay
          isLoading={showLoading}
          wrap={true}
          wrapStyle={showLoading ? { minHeight: 150 } : undefined}
          backgroundColor={
            isSmallMobile
              ? theme.colors.layoutBgColor
              : theme.colors.paymentBgColor
          }
        >
          {getPaymentDetails()}
        </LoadingSpinnerOverlay>
        <Text type="secondary" style={{ marginTop: 6, marginBottom: 8 }}>
          {t("crypto_checkout.withdrawal_fees_notice")}
        </Text>
        {expirationText}
        {paymentLinkData?.payment_url && (
          <LinkButton
            type="link"
            onClick={() => openPaymentLink()}
            style={{ paddingLeft: 0, marginTop: 6 }}
          >
            {t("crypto_checkout.check_payment_status")}
          </LinkButton>
        )}
      </>
    );
  };

  const onFinish = () => {
    if (isEmailStep) {
      setStep(Step.CHECKOUT);
      setAcceptedTerms?.(true);
    } else {
      getPaymentLink();
    }
  };

  return (
    <CheckoutForm
      form={form}
      layout="vertical"
      requiredMark={false}
      onFinish={onFinish}
    >
      {isEmailStep ? getEmailStepContent() : getCheckoutStepContent()}
    </CheckoutForm>
  );
};

export default CryptoCheckout;

enum Step {
  EMAIL = "email",
  CHECKOUT = "checkout",
}

const CountdownInput = styled(InputBox)<{
  $isSmallMobile?: number;
  $borderColor?: string;
}>`
  height: ${(p) => (p.$isSmallMobile ? 32 : 40)}px !important;
  justify-content: space-between;
  padding-right: 0;
  ${(p) =>
    p.$borderColor &&
    `
      border-color: ${p.$borderColor};
    `};
`;
const InputTitle = styled(Text)`
  margin-bottom: 8px;
`;
const AmountInput = styled.div`
  background-color: ${(p) => p.theme.colors.statisticsBgColor};
  border-radius: 6px;
  color: ${(p) => p.theme.colors.primaryText};
  display: flex;
  align-items: center;
  padding: 0 12px;
  height: 40px;
  @media (max-height: ${SMALL_MOBILE_CHECKOUT_HEIGHT}) {
    height: 32px !important;
  }
`;
const ImageWrapper = styled.div<{
  $imageSize: number;
  $isSmallMobile?: boolean;
}>`
  height: ${(p) => p.$imageSize}px;
  width: ${(p) => p.$imageSize}px;
  flex: none;
  position: relative;
  margin-left: ${(p) => (p.$isSmallMobile ? -9 : -12)}px;
`;
const ImageBorder = styled.div<{ $isSmallMobile?: boolean }>`
  border: ${(p) => (p.$isSmallMobile ? 9 : 12)}px solid
    ${(p) =>
      p.$isSmallMobile
        ? p.theme.colors.layoutBgColor
        : p.theme.colors.paymentBgColor};
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
`;
const QrImage = styled.img`
  height: 100%;
`;
const ErrorContentWrapper = styled.div`
  margin: 30px auto 0 auto;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  max-width: 300px;
`;
const WarningText = styled(Text).attrs({ type: "warning" })`
  margin-top: 4px;
  font-size: ${(p) => p.theme.typography.fontSizes.S};
`;
